'use client';

import { useRef, useState } from 'react';
import { Icon, IconType } from '../icons';
import { IconButton } from '../icon-button';
import { Button } from '../button';
import { FileDrop } from 'react-file-drop';
import { cn, FileDropProps, FileInfoProps } from 'types';

const FileInfo = ({ file, buttonVarient, onRemove, fileNameSuffix }: FileInfoProps) => {
  return (
    <div className="flex items-center gap-6 w-full">
      <div className="flex gap-1 flex-1">
        <IconButton
          ariaLabel="document"
          icon={IconType.FileOutline}
          height={24}
          width={24}
          onClick={(e) => e.preventDefault()}
        />
        <div className="file-name leading-[175%] font-semibold">
          {file?.name}
          {fileNameSuffix}
        </div>
      </div>

      <Button variant={buttonVarient} onClick={onRemove} className="!font-bold flex w-fit">
        <Icon
          icon={IconType.NewNextTrash}
          height={22}
          width={22}
          fill={buttonVarient == 'teal' ? 'var(--color-black)' : 'var(--color-white)'}
        />
        Slett
      </Button>
    </div>
  );
};

export const FileDropContainer = ({
  title = 'Legg til filer',
  label = 'Dra og slipp filer',
  displayIcon = IconType.NewNextArrowUp,
  disabled = false,
  className = '',
  fileDropClassName = '',
  fileNameSuffix = ' lastet opp',
  children,
  iconColor,
  onDrop,
  onAdd,
  onRemove,
  buttonVarient = 'teal',
}: FileDropProps) => {
  const [fileList, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList) => {
    const newFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      if (!fileList.some((x) => x.lastModified === files[i].lastModified)) {
        newFiles.push(files[i]);
      }
    }

    const updated = [...fileList, ...newFiles];
    setFiles(updated);
    return updated;
  };

  const handleDrop = (files: FileList, event: React.DragEvent<HTMLDivElement>) => {
    onDrop(handleFiles(files), event);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    onAdd(handleFiles(event.target.files), event);
  };

  const handleRemove = (event: React.MouseEvent<HTMLButtonElement>, idx: number) => {
    event.preventDefault();
    const updated = [...fileList];
    updated.splice(idx, 1);
    setFiles(updated);
    onRemove(updated);
    event.stopPropagation();
  };

  const isDropped = fileList.length > 0;

  return (
    <div>
      <input ref={fileInputRef} type="file" hidden disabled={disabled} onChange={handleInputChange} />
      <div
        className={cn(
          'rounded-[20px] border-2 border-dashed border-davyGrey transition-colors w-full',
          {
            'hover:border-black hover:bg-gray-100': !disabled && !isDropped,
            'hover:border-black cursor-not-allowed border-neutralGrey bg-lightGrey': disabled,
            'border-black bg-turquoise20': isDropped,
          },
          className
        )}
      >
        <FileDrop onTargetClick={() => fileInputRef.current?.click()} onDrop={handleDrop}>
          <div className={cn(`min-h-30 flex flex-col py-8 px-6`, fileDropClassName)}>
            {/* Drop zone */}
            <div className="flex items-center gap-6 w-full">
              <div
                className={cn('w-8 h-8 flex items-center justify-center rounded-full bg-turquoise', {
                  'bg-neutralGrey': disabled,
                })}
              >
                <Icon
                  height={24}
                  width={24}
                  icon={displayIcon}
                  fill={disabled ? 'var(--color-neutralPlus)' : iconColor}
                />
              </div>

              <div className="flex-1">
                <h2
                  className={cn('font-mulish font-extrabold text-lg leading-[175%]', { 'text-neutralPlus': disabled })}
                >
                  {title}
                </h2>
                <p
                  className={cn('font-mulish font-semibold text-base leading-[175%]', { 'text-neutralPlus': disabled })}
                >
                  {label}
                </p>
                {children}
              </div>
            </div>

            {/* Files */}
            {isDropped && (
              <>
                <div className="h-px my-3 bg-turquoise60 w-full" />
                <div className="flex flex-col gap-3 w-full">
                  {fileList.map((file, idx) => (
                    <FileInfo
                      buttonVarient={buttonVarient}
                      key={idx}
                      file={file}
                      fileNameSuffix={fileNameSuffix}
                      onRemove={(e) => handleRemove(e, idx)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </FileDrop>
      </div>
    </div>
  );
};
